import { useState, useEffect, useContext } from "react";
import { Row, Col, Tabs, Dropdown, Menu } from "antd";
import {
  EditOutlined,
  SelectOutlined,
  FileDoneOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import DataTable from "../../DataTable";
import CopyableCell from "../../CopyableCell";

import { TourContext } from "../../../context/TourContext";
import {
  clampPage,
  getStoredActiveTab,
  getStoredTabPage,
  resolveActiveTab,
  resolveTabPage,
  setStoredActiveTab,
  setStoredTabPage,
  visibleStatusTabs,
} from "../../../utils/tablePaginationStorage";

const { TabPane } = Tabs;

const dummyData = [
  {
    id: 1,
    ClaimNumber: "CLM001",
    PolicyID: "POL123456",
    DeceasedName: "Alice",
    DeceasedSurname: "Smith",
    DeceasedType: "Accident",
    ClaimAmount: 10000,
    Date: new Date("2024-01-15"),
    Status: "Pending",
  },
];

interface IClaimProps {
  claims: any;
  showEditModal: Function;
  showContinuationModal: Function;
  handleManage?: Function;
  filtered: any;
  storagePrefix?: string;
}

const ClaimTable: React.FC<IClaimProps> = ({
  claims,
  showEditModal,
  showContinuationModal,
  handleManage,
  filtered,
  storagePrefix = "claims",
}) => {
  const { refManageClaim, refEditClaim, markPageAsVisited, pages } =
    useContext(TourContext);

  const initialTab = getStoredActiveTab(storagePrefix);

  const [approved, setApproved] = useState([]);
  const [pending, setPending] = useState<any>([]);
  const [rejected, setRejected] = useState([]);
  const [paid, setPaid] = useState([]);
  const [closed, setClosed] = useState([]);
  const [filteredApproved, setFilteredApproved] = useState([]);
  const [filteredPending, setFilteredPending] = useState([]);
  const [filteredRejected, setFilteredRejected] = useState([]);
  const [filteredPaid, setFilteredPaid] = useState([]);
  const [filteredClosed, setFilteredClosed] = useState([]);

  const [state, setState] = useState({
    loading: true,
    activeKey: initialTab,
    pagination: {
      pageSize: 15,
      position: ["bottomRight"],
      total: 0,
      current: getStoredTabPage(storagePrefix, initialTab),
      hideOnSinglePage: true,
    },
    searchItem: "",
  });

  const applyTabTotal = (tabKey: string, total: number) => {
    setState((prev) => {
      if (prev.activeKey !== tabKey) {
        return { ...prev, loading: false };
      }
      const pageSize = prev.pagination.pageSize || 15;
      const current = resolveTabPage(
        storagePrefix,
        tabKey,
        prev.pagination.current || 1,
        total,
        pageSize
      );
      return {
        ...prev,
        loading: false,
        pagination: {
          ...prev.pagination,
          total,
          current,
        },
      };
    });
  };

  useEffect(() => {
    if (claims.length === 0) {
      setPending(dummyData);
      setApproved([]);
      setRejected([]);
      setPaid([]);
      setClosed([]);
    } else {
      setPending(claims.filter((item: any) => item.Status === "Pending"));
      setApproved(claims.filter((item: any) => item.Status === "Approved"));
      setRejected(claims.filter((item: any) => item.Status === "Rejected"));
      setPaid(claims.filter((item: any) => item.Status === "Paid"));
      setClosed(claims.filter((item: any) => item.Status === "Closed"));
    }

    if (filtered.length === 0) {
      setFilteredApproved([]);
      setFilteredPending([]);
      setFilteredRejected([]);
      setFilteredPaid([]);
      setFilteredClosed([]);
    } else {
      setFilteredPending(filtered.filter((item: any) => item.Status === "Pending"));
      setFilteredApproved(filtered.filter((item: any) => item.Status === "Approved"));
      setFilteredRejected(filtered.filter((item: any) => item.Status === "Rejected"));
      setFilteredPaid(filtered.filter((item: any) => item.Status === "Paid"));
      setFilteredClosed(filtered.filter((item: any) => item.Status === "Closed"));
    }

    setState((prev) => ({ ...prev, loading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, filtered]);

  const getTabCount = (key: string) => {
    const useFiltered = filtered.length > 0;
    switch (key) {
      case "1":
        return useFiltered ? filteredPending.length : pending.length;
      case "2":
        return useFiltered ? filteredApproved.length : approved.length;
      case "3":
        return useFiltered ? filteredRejected.length : rejected.length;
      case "4":
        return useFiltered ? filteredPaid.length : paid.length;
      case "5":
        return useFiltered ? filteredClosed.length : closed.length;
      default:
        return 0;
    }
  };

  useEffect(() => {
    // Dummy pending row is display-only — do not use its count for page clamp/storage.
    if (claims.length === 0 && filtered.length === 0) {
      applyTabTotal(state.activeKey, 0);
      return;
    }
    applyTabTotal(state.activeKey, getTabCount(state.activeKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pending,
    approved,
    rejected,
    paid,
    closed,
    filteredPending,
    filteredApproved,
    filteredRejected,
    filteredPaid,
    filteredClosed,
    filtered.length,
    claims.length,
  ]);

  const handleTableChange = (pagination: any) => {
    setStoredTabPage(storagePrefix, state.activeKey, pagination.current);
    setState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize ?? prev.pagination.pageSize,
      },
    }));
  };

  const columns = [
    {
      title: "Claim No",
      dataIndex: "ClaimNumber",
      render: (v: any) => <CopyableCell value={v} />,
    },
    {
      title: "Policy Number",
      dataIndex: "PolicyID",
      render: (id: string) => <CopyableCell value={id && id.toUpperCase()} />,
    },
    {
      title: "Name",
      dataIndex: "DeceasedName",
    },
    {
      title: "Surname",
      dataIndex: "DeceasedSurname",
    },
    {
      title: "Deceased Type",
      dataIndex: "DeceasedType",
    },
    {
      title: "Claim Type",
      dataIndex: "IsExternal",
      render: (isExternalFlag: any, record: any) => {
        const external =
          isExternalFlag === true ||
          record?.ClaimType === "External" ||
          record?.Policy?.Group?.GroupType === "External";
        return external ? "External" : "Internal";
      },
    },
    {
      title: "Claim Amount",
      dataIndex: "ClaimAmount",
    },
    {
      title: "Claim Date",
      dataIndex: "Date",
      render: (date: Date) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              {handleManage && (
                <Menu.Item
                  key="manage"
                  icon={<SelectOutlined ref={refManageClaim} style={{ color: "green" }} />}
                  onClick={() => handleManage(record.id)}
                >
                  Manage Claim
                </Menu.Item>
              )}
              <Menu.Item
                key="edit"
                icon={<EditOutlined ref={refEditClaim} style={{ color: "blue" }} />}
                onClick={() => showEditModal(record)}
              >
                Edit Claim
              </Menu.Item>
              {record.Status === "Closed" && record.Continuation === false && (
                <Menu.Item
                  key="continuation"
                  icon={<FileDoneOutlined style={{ color: "orange" }} />}
                  onClick={() => showContinuationModal(record)}
                >
                  Continuation Policy
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <MoreOutlined
            rotate={90}
            style={{ cursor: "pointer", fontSize: "16px", color: "#000" }}
          />
        </Dropdown>
      ),
    },
  ];

  const tabPanes = [
    {
      key: "1",
      title: "Pending",
      content: (
        <DataTable
          persistKey="claims"
          dataSource={filteredPending.length > 0 ? filteredPending : pending}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: getTabCount("1"),
    },
    {
      key: "2",
      title: "Approved",
      content: (
        <DataTable
          persistKey="claims"
          dataSource={filteredApproved.length > 0 ? filteredApproved : approved}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: getTabCount("2"),
    },
    {
      key: "3",
      title: "Rejected",
      content: (
        <DataTable
          persistKey="claims"
          dataSource={filteredRejected.length > 0 ? filteredRejected : rejected}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: getTabCount("3"),
    },
    {
      key: "4",
      title: "Paid",
      content: (
        <DataTable
          persistKey="claims"
          dataSource={filteredPaid.length > 0 ? filteredPaid : paid}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: getTabCount("4"),
    },
    {
      key: "5",
      title: "Closed",
      content: (
        <DataTable
          persistKey="claims"
          dataSource={filteredClosed.length > 0 ? filteredClosed : closed}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: getTabCount("5"),
    },
  ];

  const visiblePanes = visibleStatusTabs(tabPanes);
  const activeKey = resolveActiveTab(state.activeKey, visiblePanes);

  const changeTab = (key: string) => {
    setStoredActiveTab(storagePrefix, key);
    const total = getTabCount(key);
    const pageSize = state.pagination.pageSize || 15;
    const storedPage = getStoredTabPage(storagePrefix, key);
    const current = clampPage(storedPage, total, pageSize);
    setStoredTabPage(storagePrefix, key, current);

    setState((prev) => ({
      ...prev,
      activeKey: key,
      loading: false,
      pagination: {
        ...prev.pagination,
        current,
        total,
      },
    }));
  };

  useEffect(() => {
    if (pages.ManageMember) {
      markPageAsVisited("ClaimTable");
    }
  }, []);

  return (
    <>
      <Row>
        <Col span={24}>
          <Tabs
            type="card"
            activeKey={activeKey}
            onChange={changeTab}
          >
            {visiblePanes.map((pane) => (
              <TabPane
                key={pane.key}
                tab={
                  <>
                    <span>{pane.title}</span>
                    <span
                      style={{
                        color: "#B0B0B0",
                        fontWeight: "300",
                        marginLeft: "3px",
                        fontSize: "12px",
                      }}
                    >
                      ({pane.count})
                    </span>
                  </>
                }
              >
                {pane.content}
              </TabPane>
            ))}
          </Tabs>
        </Col>
      </Row>
    </>
  );
};

export default ClaimTable;
